import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe, Stripe, StripeElements } from '@stripe/stripe-js';
import { Button } from '../Button/Button';
import { FormEvent, useState } from 'react';

const stripePromise = loadStripe(import.meta.env.VITE_APP_STRIPE_KEY);

interface StripePaymentFormInnerProps {
  onSuccess: (stripe: Stripe, elements: StripeElements) => void;
  onCancel?: () => void;
  submitLabel?: string;
  isLoading?: boolean;
}

const StripePaymentFormInner = ({
  onSuccess,
  onCancel,
  submitLabel = 'Add Card',
  isLoading = false,
}: StripePaymentFormInnerProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState<string | undefined>();
  const [isProcessing, setIsProcessing] = useState(false);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!stripe || !elements) return;

    setIsProcessing(true);
    setError(undefined);

    const { error: submitError } = await elements.submit();
    if (submitError) {
      setError(submitError.message);
      setIsProcessing(false);
      return;
    }

    await onSuccess(stripe, elements);
    setIsProcessing(false);
  };

  const busy = isProcessing || isLoading;

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement />

      {error && (
        <div className="text-system-error font-bold py-2">{error}</div>
      )}

      <div className="flex items-center justify-between pt-2">
        {onCancel && (
          <Button
            onClick={onCancel}
            kind="secondary"
            type="button"
            disabled={busy}
          >
            Cancel
          </Button>
        )}
        <Button
          type="submit"
          disabled={!stripe || busy}
          loading={busy}
          className={onCancel ? '' : 'w-full'}
        >
          {submitLabel}
        </Button>
      </div>
    </form>
  );
};

interface StripePaymentFormProps {
  clientSecret: string;
  onSuccess: (stripe: Stripe, elements: StripeElements) => void;
  onCancel?: () => void;
  submitLabel?: string;
  isLoading?: boolean;
}

export const StripePaymentForm = ({
  clientSecret,
  onSuccess,
  onCancel,
  submitLabel,
  isLoading,
}: StripePaymentFormProps) => {
  if (!clientSecret) return null;

  return (
    <Elements options={{ clientSecret }} stripe={stripePromise}>
      <StripePaymentFormInner
        onSuccess={onSuccess}
        onCancel={onCancel}
        submitLabel={submitLabel}
        isLoading={isLoading}
      />
    </Elements>
  );
};

interface StripePaymentFormDeferredProps {
  onSuccess: (stripe: Stripe, elements: StripeElements) => void;
  onCancel?: () => void;
  submitLabel?: string;
  isLoading?: boolean;
}

export const StripePaymentFormDeferred = ({
  onSuccess,
  onCancel,
  submitLabel = 'Start Free Trial & Add Card',
  isLoading,
}: StripePaymentFormDeferredProps) => {
  return (
    <Elements
      options={{
        mode: 'setup',
        currency: 'usd',
        paymentMethodTypes: ['card'],
      }}
      stripe={stripePromise}
    >
      <StripePaymentFormInner
        onSuccess={onSuccess}
        onCancel={onCancel}
        submitLabel={submitLabel}
        isLoading={isLoading}
      />
    </Elements>
  );
};
